import { useEffect, useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Phone, WhatsappLogo, Envelope, MapPin, Handshake, Warning, Scales, Coins, Trophy, ChartLineUp, Buildings } from '@phosphor-icons/react';

const METRICS = [
  { key: 'calls', label: 'اتصالات', icon: Phone, tone: 'text-blue-600' },
  { key: 'whatsapp', label: 'واتساب', icon: WhatsappLogo, tone: 'text-emerald-600' },
  { key: 'emails', label: 'إيميلات', icon: Envelope, tone: 'text-slate-600' },
  { key: 'visits', label: 'زيارات', icon: MapPin, tone: 'text-amber-600' },
  { key: 'meetings', label: 'اجتماعات', icon: Handshake, tone: 'text-purple-600' },
  { key: 'escalations', label: 'رفع/مالية', icon: Warning, tone: 'text-orange-600' },
  { key: 'legal', label: 'قانونية', icon: Scales, tone: 'text-red-600' },
];

export default function EmployeeKPI() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/kpi/agents', { params: { month } }).then(({ data }) => setData(data)),
      api.get('/kpi/my-scorecard', { params: { month } }).then(({ data }) => setMine(data.scorecard)),
    ]).finally(() => setLoading(false));
  }, [month]);

  if (loading || !data) return <div className="text-slate-500 text-sm">جاري التحميل...</div>;

  const canSeeAll = ['admin', 'manager', 'supervisor'].includes(user?.role);
  const agents = canSeeAll ? data.agents.filter(a => a.role !== 'viewer') : data.agents.filter(a => a.user_id === user?.id);
  const top = agents[0];

  return (
    <div className="space-y-6" data-testid="employee-kpi-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Employee Performance</div>
          <h1 className="text-3xl font-bold text-slate-900">أداء الموظفين</h1>
          <p className="text-sm text-slate-500 mt-1">بطاقة أداء شخصية لكل موظف تحصيل</p>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">الشهر</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="month-picker" />
        </div>
      </div>

      {/* My Scorecard */}
      {mine && (
        <Card className="p-6 border border-slate-200 rounded-md shadow-none bg-gradient-to-l from-slate-900 to-slate-800 text-white">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D4B683] mb-1">Your Scorecard</div>
              <div className="text-2xl font-bold">{mine.name}</div>
              <div className="text-sm text-white/60 mt-1">أداؤك خلال {month}</div>
            </div>
            <div className="text-end">
              <div className="text-4xl font-bold text-[#D4B683] tabular-nums">{mine.total_actions}</div>
              <div className="text-xs text-white/60">إجراء متابعة</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="flex items-center gap-2 text-white/70 text-xs mb-1"><Buildings size={14} /> فواتير عملت عليها</div>
              <div className="text-2xl font-bold tabular-nums">{mine.invoices_touched}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-white/70 text-xs mb-1"><ChartLineUp size={14} /> مدفوعات مؤمّنة</div>
              <div className="text-2xl font-bold tabular-nums text-emerald-400">{mine.payments_secured}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-white/70 text-xs mb-1"><Coins size={14} /> مبلغ محصّل</div>
              <div className="text-2xl font-bold tabular-nums text-[#D4B683]">{formatMoney(mine.amount_collected)}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-white/70 text-xs mb-1"><Trophy size={14} /> ترتيبك</div>
              <div className="text-2xl font-bold tabular-nums">
                #{agents.findIndex(a => a.user_id === user?.id) + 1}
                <span className="text-sm text-white/60 me-1">/ {agents.length}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Leaderboard */}
      {canSeeAll && (
        <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Trophy size={18} className="text-[#B4935A]" />
            <div className="font-semibold text-slate-900">لوحة المتصدرين</div>
            {top && <div className="text-xs text-slate-500 me-auto">الأول: {top.name}</div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">#</th>
                  <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">الموظف</th>
                  <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">الفرع</th>
                  {METRICS.map(m => (
                    <th key={m.key} className="text-center p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">
                      <div className="flex flex-col items-center gap-1">
                        <m.icon size={14} className={m.tone} />
                        <span>{m.label}</span>
                      </div>
                    </th>
                  ))}
                  <th className="text-end p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">مدفوعات</th>
                  <th className="text-end p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">مبلغ محصّل</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a, i) => (
                  <tr key={a.user_id} className="border-t border-slate-100" data-testid={`agent-row-${a.user_id}`}>
                    <td className="p-3">
                      {i < 3 ? (
                        <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-[#B4935A] text-white' : i === 1 ? 'bg-slate-400 text-white' : 'bg-amber-700 text-white'
                        }`}>{i + 1}</span>
                      ) : (
                        <span className="text-slate-500">{i + 1}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500 mono">{a.email}</div>
                    </td>
                    <td className="p-3 text-slate-600">{a.branch || '—'}</td>
                    {METRICS.map(m => (
                      <td key={m.key} className="p-3 text-center tabular-nums">
                        {a[m.key] > 0 ? <span className={m.tone + ' font-medium'}>{a[m.key]}</span> : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="p-3 text-end tabular-nums font-medium text-emerald-600">{a.payments_secured}</td>
                    <td className="p-3 text-end tabular-nums font-medium">{formatMoney(a.amount_collected)}</td>
                  </tr>
                ))}
                {agents.length === 0 && (
                  <tr><td colSpan={11} className="p-8 text-center text-slate-500">لا يوجد نشاط في هذا الشهر</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
